/**
 * AOP 2027 Planning — how the targets' figures are added up for display, shared by the targets
 * table, its cost chart and the Overview's company block, so a column, a bar and a tile can never
 * disagree about the same month. Every figure itself comes from the proxy
 * (`proxy-server/lib/aopTargets.js`).
 */
import type { MonthGroup } from '../internalPlayground/OrgChartTable';
import { AOP_HISTORY_END, AOP_PLAN_YEAR } from './aopColumns';

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** 'YYYY-MM' → "Jan '27", as in the tree's header. */
export const monthLabel = (month: string) => `${MONTH_ABBR[Number(month.slice(5, 7)) - 1]} '${month.slice(2, 4)}`;

/** One month of figures — a department's, or the company's (any figure may then be blank). */
export interface TargetsMonthFigures {
  /** Optional: an older proxy revision sends no parts. */
  committedFte?: number | null;
  draftFte?: number | null;
  planFte: number | null;
  budgetFte: number | null;
  budgetCost: number | null;
  baselineCost: number | null;
  draftCost: number | null;
  planCost: number | null;
}
export type TargetsMonths = Record<string, TargetsMonthFigures>;
export type FteField = 'committedFte' | 'draftFte' | 'planFte' | 'budgetFte';
export type CostField = 'budgetCost' | 'baselineCost' | 'draftCost' | 'planCost';

/** `end` is the month a point-in-time figure is read at; `months` are summed for cost. */
export type Column = { id: string; label: string; end: string; months: string[]; total?: boolean };

/** `a − b` rounded to `digits` decimals (what is shown), or null when either is missing — so a
 * gap of a few cents is "0", never a red "+0 €". */
export const diff = (a: number | null | undefined, b: number | null | undefined, digits: number) =>
  typeof a === 'number' && typeof b === 'number' ? Math.round((a - b) * 10 ** digits) / 10 ** digits || 0 : null;

/** The plan months the targets show: everything after the history months. */
export const planMonths = (groups: MonthGroup[]) => groups.flatMap((g) => g.months).filter((m) => m > AOP_HISTORY_END);

/** The page's columns after the history months: an expanded group's months one by one, a
 * collapsed group as one column. */
export function toColumns(columns: string[], groups: MonthGroup[], expandedGroupKeys: Set<string>): Column[] {
  return columns
    .map((key) => {
      const collapsed = groups.find((g) => g.key === key && !expandedGroupKeys.has(g.key));
      return collapsed
        ? { id: key, label: collapsed.label, end: key, months: collapsed.months }
        : { id: key, label: monthLabel(key), end: key, months: [key] };
    })
    .filter((c) => c.end > AOP_HISTORY_END);
}

/** The plan year as one column: December's FTE, the year's costs. Null when the page has none of it. */
export function yearColumn(groups: MonthGroup[]): Column | null {
  const months = groups.flatMap((g) => g.months).filter((m) => m.startsWith(`${AOP_PLAN_YEAR}-`));
  if (months.length === 0) return null;
  return { id: 'fy', label: `FY '${AOP_PLAN_YEAR.slice(2)}`, end: months[months.length - 1], months, total: true };
}

/** A cost over a column: the sum of its months — null unless EVERY month has one, so a column
 * never compares a one-month budget with a three-month plan. */
export function costOver(months: TargetsMonths, column: Column, field: CostField): number | null {
  const values = column.months.map((m) => months[m]?.[field]);
  return values.every((v): v is number => typeof v === 'number') ? values.reduce((a, b) => a + b, 0) : null;
}

/** Plan cost over a column as its two parts as shown (the baseline to the euro; the drafts already
 * are), so the table adds up — the proxy rounds plan cost per month, not its parts. The proxy's
 * own figure where a part is blank. */
export function planCostOver(months: TargetsMonths, column: Column): number | null {
  const baseline = costOver(months, column, 'baselineCost');
  const drafts = costOver(months, column, 'draftCost');
  return baseline !== null && drafts !== null ? Math.round(baseline) + Math.round(drafts) : costOver(months, column, 'planCost');
}

/** A cost row's figure over a column; plan cost as the sum of its parts (see `planCostOver`). */
export const costAt = (months: TargetsMonths, column: Column, field: CostField) =>
  field === 'planCost' ? planCostOver(months, column) : costOver(months, column, field);

/** An FTE figure at a column's end month. */
export const fteAt = (months: TargetsMonths, column: Column, field: FteField) => months[column.end]?.[field] ?? null;

/** Plan cost minus budget cost over a column's months, to the euro. */
export const costGap = (months: TargetsMonths, column: Column) =>
  diff(costAt(months, column, 'planCost'), costAt(months, column, 'budgetCost'), 0);

/** Plan FTE minus budget FTE at a column's end month, to the shown decimal. */
export const fteGap = (months: TargetsMonths, column: Column) =>
  diff(fteAt(months, column, 'planFte'), fteAt(months, column, 'budgetFte'), 1);

/** One month of the cost chart: the plan's two parts stacked, the budget beside them. */
export interface CostChartPoint {
  month: string;
  label: string;
  baseline: number | null;
  drafts: number | null;
  budget: number | null;
}

/** The cost chart's months: every plan month, with its baseline, drafts and budget (as shown).
 * A month missing either part has neither: the plan is unknown, and one part alone would draw it
 * as if the other were 0 (hidden or unpriced drafts would read as "under budget"). */
export function costChartPoints(months: TargetsMonths, groups: MonthGroup[]): CostChartPoint[] {
  return planMonths(groups).map((month) => {
    const m = months[month];
    const round = (v: number | null | undefined) => (typeof v === 'number' ? Math.round(v) : null);
    const [baseline, drafts] = [round(m?.baselineCost), round(m?.draftCost)];
    const known = baseline !== null && drafts !== null;
    return { month, label: monthLabel(month), baseline: known ? baseline : null, drafts: known ? drafts : null, budget: round(m?.budgetCost) };
  });
}

/** Each department's plan year: plan cost minus budget cost, biggest overspend first. A department
 * without both figures for the whole year is listed apart (`missing`), never drawn as 0. */
export function departmentYearGaps(byDepartment: Record<string, { months: TargetsMonths }>, groups: MonthGroup[]) {
  const year = yearColumn(groups);
  const entries = Object.entries(byDepartment).map(([label, t]) => ({ label, gap: year ? costGap(t.months, year) : null }));
  return {
    gaps: entries.filter((e): e is { label: string; gap: number } => e.gap !== null).sort((a, b) => b.gap - a.gap),
    missing: entries.filter((e) => e.gap === null).map((e) => e.label),
  };
}
